/* WeQuote Dev Board — Kanban (renderBoard + kanbanCardHtml, faithful port).
 * Full column set (Needs Review → Backlog → Design → Design Complete → per-dev
 * lanes → Ready to Test → Testing Complete → Live → On Hold → Archived);
 * ideas are excluded (they live only on the Ideas page — verify §63).
 * Drag between columns runs moveStatus (design gate + checklist-before-Live
 * gate); dragging a no-design card dims the design columns. Clicking a card
 * opens the shared pop-up. LIVE cards show the simplified shipped state. */
import React, { useState } from 'react'
import { useDevBoard } from '../DevBoardProvider'
import * as M from '../core/mutations'
import type { Feature } from '../core/types'
import { kanbanCols, DL, EL, PL, isProgress, timeLabel, POOLCOLS, isPool } from '../core/constants'
import { fmtDate, daysLeft, elapsedLabel, rankCls } from '../core/dates'
import { rankMap } from '../core/logic'
import { TitleWithBug, AssignedPill, DateLine, ChecklistChip, RequestLine, SourceLine, RequesterWho } from './parts'
import { useQuickEdit } from './QuickEdit'
import { IcChevronLeft, IcChevronRight, IcCalendar } from '../icons'
import { useAuth } from '../../../../contexts/AuthContext'

/** Collapsed-column choices are PER USER: stored in this browser under the
 *  logged-in agent's id, so one person's collapse never affects anyone else. */
const collapseKey = (agentId: string) => 'wq-devboard-collapsed-cols:' + (agentId || 'anon')
const readCollapsed = (agentId: string): Record<string, boolean> => {
  try { return JSON.parse(localStorage.getItem(collapseKey(agentId)) || '{}') } catch { return {} }
}

const KanbanCard: React.FC<{ feat: Feature; rank: number }> = ({ feat, rank }) => {
  const { mutate, openCardModal, openSubModal } = useDevBoard()
  const prog = isProgress(feat.status)
  const num = rank
    ? <span className={'pnum-badge ' + rankCls(rank)}>{rank}</span>
    : feat.status === 'needsreview'
      ? <span className="rv-tag">NEW</span>
      : feat.status === 'archived'
        ? <span className="rv-tag arch">ARCHIVED</span>
        : <span className="pnum-badge rank-grey">✓</span>
  const qe = useQuickEdit(feat) // click a field to edit it in place
  const head = (
    <div className="kc-head">
      <div className="ch-left">{num}{feat.time && <span className={'badge effort-' + feat.eff + ' qe-click'} title="Click to change the estimate" onClick={qe.start('time')}>{EL[feat.eff]}</span>}</div>
      <span className={'badge priority-' + feat.pri + ' qe-click'} title="Click to change priority" onClick={qe.start('pri')}>{PL[feat.pri]}</span>
    </div>
  )
  const modChip = <span className={'mod-chip' + (feat.module ? '' : ' none') + ' qe-click'} title="Module — click to change" onClick={qe.start('module')}>{feat.module || 'No module'}</span>
  const schedBtn = isPool(feat.status)
    ? <button className="dv-quick-btn sched kc-sched" title="Schedule — pick a dev and start date" onClick={(e) => { e.stopPropagation(); openSubModal({ kind: 'schedule', id: feat.id }) }}><IcCalendar size={11} /> Schedule</button>
    : null
  const title = <div className="kc-title"><TitleWithBug feat={feat} /></div>

  if (feat.status === 'live') {
    return (
      <>
        {qe.editor}
        {title}
        <div className="kc-live">
          <div className="kcl-row"><span className="kcl-k">Requested</span><span className="kcl-v">{feat.dateAdded ? fmtDate(feat.dateAdded) : 'Not set'}</span></div>
          <div className="kcl-row"><span className="kcl-k">Completed</span><span className="kcl-v">{feat.completedOn ? fmtDate(feat.completedOn) : 'Not set'}</span></div>
          <div className="kcl-row kcl-gap"><span className="kcl-k">Time to live</span><span className="kcl-v">{elapsedLabel(feat.dateAdded, feat.completedOn)}</span></div>
        </div>
        <div className="kc-row"><AssignedPill dev={feat.dev} onClick={qe.start('dev')} />{modChip}<span className="status-pill s-live">Live</span></div>
      </>
    )
  }
  if (prog) {
    const dl = feat.status === 'onhold' ? { txt: 'ON HOLD', cls: 'red' } : daysLeft(feat.deadline)
    const barCls = feat.status === 'onhold' ? 'pbar-fill hold' : 'pbar-fill'
    return (
      <>
        {qe.editor}
        {head}{title}
        <div className="kc-modrow">{modChip}{schedBtn}</div>
        <div className="est-sm">Est {feat.time ? timeLabel(feat.time) : 'TBD'}</div>
        <div className="pbar"><div className={barCls} style={{ width: (feat.progress || 0) + '%' }} /></div>
        <div className="pmeta"><span className="pct">{feat.progress || 0}%</span><span className={'dleft ' + dl.cls}>{dl.txt}</span></div>
        <DateLine feat={feat} onDeadlineClick={qe.start('deadline')} />
        <div className="kc-row">
          <AssignedPill dev={feat.dev} onClick={qe.start('dev')} />
          {feat.status === 'custreview' && <span className="status-pill s-custreview">Waiting on Customer</span>}
          {feat.status === 'testing' && <span className="status-pill s-testing">Ready to Test</span>}
          {feat.status === 'testcomplete' && <span className="status-pill s-testcomplete">Testing Complete</span>}
          {feat.status === 'onhold' && <button className="face-resume" onClick={(e) => { e.stopPropagation(); mutate((fs) => M.resumeCard(fs, feat.id)) }}>Resume</button>}
          <ChecklistChip feat={feat} />
        </div>
        {feat.pnotes
          ? <div className="pnote-trunc" style={{ textAlign: 'left' }}>{feat.pnotes}</div>
          : <span className="pnote-add" onClick={(e) => { e.stopPropagation(); openCardModal(feat.id) }}>Add progress note</span>}
      </>
    )
  }
  if (feat.status === 'archived') {
    return (
      <>
        {qe.editor}
        {head}{title}
        <div className="kc-modrow">{modChip}{schedBtn}</div>
        <div className="rv-preview">{feat.notes}</div>
        {feat.archiveReason && <div className="arch-reason">Archived: {feat.archiveReason}</div>}
        <SourceLine feat={feat} />
      </>
    )
  }
  return (
    <>
      {head}{title}
      {feat.status === 'design' && (
        <>
          <div className="pbar" style={{ marginTop: 6 }}><div className="pbar-fill" style={{ width: (feat.designProgress || 0) + '%' }} /></div>
          <div className="pmeta"><span className="pct">{feat.designProgress || 0}% designed</span></div>
        </>
      )}
      <div className="notes-trunc">{feat.notes}</div>
      {feat.status !== 'needsreview' && <div className="est-plain">Est {feat.time ? timeLabel(feat.time) : 'TBD'}</div>}
      <DateLine feat={feat} onDeadlineClick={qe.start('deadline')} />
      {feat.status === 'needsreview' && <RequesterWho feat={feat} />}
      <RequestLine feat={feat} />
      <div className="kc-row">
        <span className={'status-pill s-' + feat.status}>{kanbanCols().find((c) => c.id === feat.status)?.label || feat.status}</span>
        {feat.dev && DL[feat.dev] && <span className="assigned-text">{DL[feat.dev]}</span>}
        <ChecklistChip feat={feat} />
      </div>
    </>
  )
}

export const BoardView: React.FC = () => {
  const { cards, mutate, openCardModal } = useDevBoard()
  const { currentAgent } = useAuth()
  const agentId = String(currentAgent?.id || '')
  const [dragId, setDragId] = useState<number | null>(null)
  const [dragNoDesign, setDragNoDesign] = useState(false)
  const [overCol, setOverCol] = useState<string | null>(null)
  const [collapsedCols, setCollapsedCols] = useState<Record<string, boolean>>(() => readCollapsed(agentId))
  const toggleCol = (id: string) => {
    setCollapsedCols((prev) => {
      const next = { ...prev, [id]: !prev[id] }
      try { localStorage.setItem(collapseKey(agentId), JSON.stringify(next)) } catch { /* per-user convenience only */ }
      return next
    })
  }
  const rmap = rankMap(cards)
  const boardCards = cards.filter((f) => f.status !== 'idea') // ideas live only on the Ideas page
  const endDrag = () => { setDragId(null); setDragNoDesign(false); setOverCol(null) }
  return (
    <div id="boardView">
      <div className="board">
        {kanbanCols().map((col) => {
          const items = boardCards.filter((x) => x.status === col.id).sort((a, b) => a.po - b.po)
          const dimmed = dragNoDesign && (col.id === 'design' || col.id === 'designcomplete')
          const collapsed = !!collapsedCols[col.id]
          if (collapsed) {
            return (
              <div
                key={col.id}
                className={'column col-collapsed ' + col.cls + (dimmed ? ' col-dimmed' : '') + (overCol === col.id && !dimmed && dragId != null ? ' col-drop' : '')}
                title={col.label + ' — click to expand'}
                onClick={() => toggleCol(col.id)}
                onDragOver={(e) => { if (dragId != null && !dimmed) { e.preventDefault(); if (overCol !== col.id) setOverCol(col.id) } }}
                onDragLeave={() => setOverCol((c) => (c === col.id ? null : c))}
                onDrop={(e) => { e.preventDefault(); if (dragId != null && !dimmed) mutate((fs) => (POOLCOLS.indexOf(col.id) > -1 ? M.moveToBand(fs, dragId, col.id) : M.moveStatus(fs, dragId, col.id))); endDrag() }}
              >
                <button className="col-collapse-btn" title="Expand" onClick={(e) => { e.stopPropagation(); toggleCol(col.id) }}><IcChevronRight size={12} /></button>
                <span className="col-vlabel">{col.label}</span>
                <span className="col-cnt">{items.length}</span>
              </div>
            )
          }
          return (
            <div key={col.id} className={'column ' + col.cls + (dimmed ? ' col-dimmed' : '')}>
              <div className="col-hdr">
                <span>{col.label}</span>
                <span className="col-hdr-right">
                  <span className="col-cnt">{items.length}</span>
                  <button className="col-collapse-btn" title="Collapse column" onClick={() => toggleCol(col.id)}><IcChevronLeft size={12} /></button>
                </span>
              </div>
              <div
                className={'cards' + (overCol === col.id && !dimmed && dragId != null ? ' drop-target' : '')}
                onDragOver={(e) => { if (dragId != null && !dimmed) { e.preventDefault(); if (overCol !== col.id) setOverCol(col.id) } }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOverCol((c) => (c === col.id ? null : c)) }}
                onDrop={(e) => { e.preventDefault(); if (dragId != null && !dimmed) mutate((fs) => M.moveStatus(fs, dragId, col.id)); endDrag() }}
              >
                {items.length === 0 ? (
                  <div className="empty-col">Drop cards here</div>
                ) : (
                  items.map((feat) => (
                    <div
                      key={feat.id}
                      className={'card' + (feat.status === 'live' ? ' card-live' : '') + (dragId === feat.id ? ' dragging' : '')}
                      draggable
                      onDragStart={(e) => { setDragId(feat.id); setDragNoDesign(feat.requiresDesign === false); e.dataTransfer.effectAllowed = 'move' }}
                      onDragEnd={endDrag}
                      onClick={() => openCardModal(feat.id)}
                    >
                      <KanbanCard feat={feat} rank={rmap[feat.id] || 0} />
                    </div>
                  ))
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
